import { AnimatedContentLoader, Button, SearchInput, TextTooltip } from '@components'
import { useBusinessContext, useMobileEmbed } from '@context'
import {
  SlideContent,
  SlideFooter,
  SlideTitle,
  Spacer
} from '@pages/BankIntegrationOnboardingPage/slides/slide.styled'
import {
  EnableBankingCountry,
  EnableBankingPSUType,
  fetchInstitutions,
  fetchRequisition,
  getDefaultEnableBankingCountry
} from '@query'
import { motion } from 'framer-motion'
import countries from 'i18n-iso-countries'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaArrowRight,
  FaCheck,
  FaCheckCircle,
  FaChevronDown,
  FaQuestionCircle,
  FaUser
} from 'react-icons/fa'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import { HolviAuthorizeButton } from '@containers/HolviAuthorizeButton/HolviAuthorizeButton.tsx'
import { filterInstitutionsByName, hasNoInstitutionMatches } from '@utils'
import { getRequisitionRedirectURL } from '@root/utils/getRequisitionRedirectURL'

interface Props {
  goBack?: () => void
  overrideRedirectUrl?: string
  overrideErrorRedirectUrl?: string
  onNoBankAccount?: () => void
  title?: string
  subtitle?: string
}

export const AspspsSlide: React.FC<Props> = ({
  goBack,
  overrideRedirectUrl,
  overrideErrorRedirectUrl,
  onNoBankAccount,
  title,
  subtitle
}) => {
  const {
    t,
    i18n: { language: currentLanguage }
  } = useTranslation()
  const [selected, setSelected] = useState<[string, string]>()
  const [bankSearch, setBankSearch] = useState('')
  const [psuType, setPsuType] = useState<EnableBankingPSUType>(EnableBankingPSUType.BUSINESS)
  const { slug, data: business } = useBusinessContext()
  const defaultCountry = getDefaultEnableBankingCountry(business?.country)
  const [countryOverride, setCountry] = useState<EnableBankingCountry>()
  const country = countryOverride ?? defaultCountry
  const {
    isEmbedded,
    redirectURL: embeddedRedirectUrl,
    errorRedirectURL: embeddedErrorRedirectUrl
  } = useMobileEmbed()

  const { data: institutions, isLoading } = useQuery({
    queryKey: [slug, 'institutions', country, psuType],
    queryFn: () => fetchInstitutions({ slug, country, psuType })
  })

  const defaultRedirects = getRequisitionRedirectURL(
    isEmbedded,
    slug,
    embeddedRedirectUrl,
    embeddedErrorRedirectUrl
  )
  const redirectUrl = overrideRedirectUrl ?? defaultRedirects.redirectUrl
  const errorRedirectUrl = overrideErrorRedirectUrl ?? defaultRedirects.errorRedirectUrl

  const { data: requisition, isFetching: isRequisitionLoading } = useQuery({
    queryKey: [slug, 'requisition', selected, psuType, redirectUrl, errorRedirectUrl],
    queryFn: () =>
      fetchRequisition({
        slug,
        aspspCountry: selected?.[0],
        aspspName: selected?.[1],
        psuType: psuType,
        redirectUrl,
        errorRedirectUrl
      }),
    ...{
      enabled: !!selected
    }
  })

  const isLinkDisabled = isRequisitionLoading || !requisition?.url
  const isHolviSelected = selected?.[1]?.toLowerCase() === 'holvi'
  const isPersonal = psuType === EnableBankingPSUType.PERSONAL
  const visibleInstitutions = filterInstitutionsByName(institutions, bankSearch)
  const hasNoBankMatches = hasNoInstitutionMatches(institutions, bankSearch, isLoading)
  const countryName = countries.getName(country, currentLanguage) ?? country
  const countryOptions = Object.values(EnableBankingCountry)
    .map(code => ({
      code,
      name: countries.getName(code, currentLanguage) ?? code
    }))
    .sort((a, b) => a.name.localeCompare(b.name, currentLanguage))

  const selectCountry = (next: EnableBankingCountry) => {
    setCountry(next)
    setSelected(undefined)
  }

  const togglePsuType = () => {
    setPsuType(isPersonal ? EnableBankingPSUType.BUSINESS : EnableBankingPSUType.PERSONAL)
    setSelected(undefined)
  }

  return (
    <>
      <SlideHeader>
        <SlideTitle>{title ?? t('settings.integrations.bank.createModalTitle')}</SlideTitle>
        {subtitle && <SlideSubtitle>{subtitle}</SlideSubtitle>}
      </SlideHeader>
      <BankSearch>
        <SearchInput
          name="onboarding-bank-search"
          autoFocus
          autoComplete="off"
          hideErrorText
          delay={0}
          placeholder={t('settings.integrations.bank.searchPlaceholder')}
          aria-label={t('settings.integrations.bank.searchPlaceholder')}
          onSearch={setBankSearch}
        />
      </BankSearch>
      <FilterRow>
        <CountryPicker>
          <CountryChip>
            <CountryPrefix>{t('settings.integrations.bank.countryLabel')}</CountryPrefix>
            <CountryName>{countryName}</CountryName>
            <CountryChevron />
          </CountryChip>
          <CountrySelect
            aria-label={t('settings.integrations.bank.selectCountry')}
            value={country}
            onChange={event => selectCountry(event.target.value as EnableBankingCountry)}
          >
            {countryOptions.map(option => (
              <option key={option.code} value={option.code}>
                {option.name}
              </option>
            ))}
          </CountrySelect>
        </CountryPicker>
        <FilterDivider />
        <PsuPill
          type="button"
          $active={isPersonal}
          aria-pressed={isPersonal}
          onClick={togglePsuType}
        >
          {isPersonal ? <FaCheck /> : <FaUser />}
          {t('settings.integrations.bank.psuLabel')}
        </PsuPill>
        <TextTooltip tooltip={t('settings.integrations.bank.psuTooltip')}>
          <HelpIcon />
        </TextTooltip>
      </FilterRow>
      <SlideContent>
        <AnimatedContentLoader isLoading={isLoading}>
          {hasNoBankMatches ? (
            <NoBankMatchesText>{t('settings.integrations.bank.searchEmpty')}</NoBankMatchesText>
          ) : (
            <BankList>
              {visibleInstitutions?.map(institution => {
                const isSelected = selected?.[1] === institution.name

                return (
                  <InstitutionItem
                    key={institution.name}
                    data-key={institution.name}
                    $selected={isSelected}
                    onClick={event => {
                      setSelected([institution.country, institution.name])
                      event.preventDefault()
                    }}
                  >
                    <InstitutionLogo $src={institution.logo} />
                    <InstitutionContent>
                      <InstitutionName $selected={isSelected}>{institution.name}</InstitutionName>
                    </InstitutionContent>
                    <SelectedCheck $selected={isSelected} aria-hidden />
                  </InstitutionItem>
                )
              })}
            </BankList>
          )}
          {!onNoBankAccount && (
            <CantFindBank href={t('link.docs.cantFindBank')} rel="noreferrer" target="_blank">
              <FaQuestionCircle />
              {t('settings.integrations.bank.cantFindBank')}
            </CantFindBank>
          )}
        </AnimatedContentLoader>
      </SlideContent>

      <BankSlideFooter>
        {goBack && (
          <Button type="button" onClick={() => goBack()}>
            {t('general.back')}
          </Button>
        )}
        {onNoBankAccount && (
          <Button
            type="button"
            onClick={onNoBankAccount}
            data-test="onboarding-no-bank-account-button"
          >
            {t('bankOnboarding.aspsps.noBankAccount')}
          </Button>
        )}
        <Spacer />
        {isHolviSelected ? (
          <HolviAuthorizeButton />
        ) : (
          <Button
            type="button"
            disabled={isLinkDisabled}
            showSpinner={isRequisitionLoading}
            iconRight={<FaArrowRight />}
            intent="primary"
            onClick={() => {
              if (requisition?.url) {
                window.location.href = requisition.url
              }
            }}
          >
            {selected
              ? t('settings.integrations.bank.connectBank', { bank: selected[1] })
              : t('settings.integrations.bank.selectBank')}
          </Button>
        )}
      </BankSlideFooter>
    </>
  )
}

const SlideHeader = styled.div`
  ${SlideTitle} {
    margin-bottom: 0;
  }
`

const SlideSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  line-height: 1.5;
  margin: ${({ theme }) => theme.spacing.xs}rem 0 0;
`

const BankSearch = styled.div`
  flex-shrink: 0;
  margin-top: ${({ theme }) => theme.spacing.lg}rem;

  .input-wrapper {
    margin-bottom: 0;
    border-radius: 999px;
  }
`

const FilterRow = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin: ${({ theme }) => theme.spacing.sm}rem 0 ${({ theme }) => theme.spacing.md}rem;
`

const CountryChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border: 1px solid ${({ theme }) => theme.colors.borderSoft};
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const CountryPicker = styled.div`
  position: relative;

  &:hover ${CountryChip} {
    background: ${({ theme }) => theme.colors.defaultHover};
  }
`

const CountryPrefix = styled.span`
  font-weight: 400;
`

const CountryName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
`

const CountryChevron = styled(FaChevronDown)`
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const CountrySelect = styled.select`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  border: 0;
`

const FilterDivider = styled.div`
  width: 1px;
  height: 1.125rem;
  background: ${({ theme }) => theme.colors.borderSoft};
  margin: 0 ${({ theme }) => theme.spacing.xxs}rem;
`

const PsuPill = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.borderSoft)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.neutralWhite};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ theme, $active }) => ($active ? theme.colors.neutralWhite : theme.colors.metalGray)};
  cursor: pointer;

  svg {
    font-size: ${({ theme }) => theme.fontSize.sm}rem;
  }
`

const HelpIcon = styled(FaQuestionCircle)`
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  cursor: help;
`

const BankList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const InstitutionItem = styled(motion.a).attrs({
  href: '#'
})<{ $selected: boolean }>`
  background: ${({ theme, $selected }) => ($selected ? theme.colors.neutralGray : 'transparent')};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  cursor: pointer;
  color: ${({ theme, $selected }) =>
    $selected ? theme.colors.primary : theme.colors.neutralBlack};
  text-decoration: none !important;

  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const InstitutionLogo = styled.div<{ $src: string }>`
  border: 0.1rem solid white;
  width: 2.5rem;
  height: 2.5rem;
  background: url(${({ $src }) => $src});
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 0.4rem;
  flex-shrink: 0;
`

const InstitutionContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
`

const InstitutionName = styled.div<{ $selected: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
`

const SelectedCheck = styled(FaCheckCircle)<{ $selected: boolean }>`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.125rem;
  opacity: ${({ $selected }) => ($selected ? 1 : 0)};
`

const NoBankMatchesText = styled.p`
  margin: 0;
  padding: 2rem ${({ theme }) => theme.spacing.sm}rem 1rem;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.md}rem;
`

const CantFindBank = styled.a`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.sm}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.px14}rem;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const BankSlideFooter = styled(SlideFooter)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`
